import { encounterTypeName } from '../gyn/encounters/constants/encounterTypes'
import { EncounterStatus } from '../gyn/encounters/types/base'
import { EncounterType } from '../gyn/encounters/types/encounterType'

// §6.4.1: Timely filing color scheme — mirrors the DOS badge in the billing dashboard.
export const dosColorScheme = (days: number): string => {
  if (days < 30) return 'green'
  if (days < 50) return 'yellow'
  if (days < 65) return 'orange'
  return 'red'
}

/**
 * Compute days elapsed since a YYYY-MM-DD date string.
 * Returns null when the input is absent or unparseable.
 */
export const getDaysSinceDOS = (dateStr: string | null | undefined): number | null => {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return null
  return Math.floor((Date.now() - d.getTime()) / 86_400_000)
}

// §6.4.3: Batch timing thresholds in days from DOS.
export const BATCH_THRESHOLDS: Partial<Record<EncounterType, number>> = {
  [EncounterType.ANTEPARTUM]: 60,
  [EncounterType.BIRTHING_CENTER_ADMISSION]: 1,
  [EncounterType.POSTPARTUM]: 45,
  [EncounterType.ENCOUNTER]: 45,
  [EncounterType.PHYSICAL]: 45,
  [EncounterType.WELL_WOMAN]: 45,
  [EncounterType.ULTRASOUND]: 30,
}

// §6.4.3: Labor management billing — separate from delivery claim; submit within 30 days
// of labor completion per AMA 2027 framework. No distinct encounter type exists for labor
// management (it falls under BIRTHING_CENTER_ADMISSION), so this threshold is checked
// independently from the 1-day delivery claim alert.
export const LABOR_MANAGEMENT_BATCH_THRESHOLD = 30

export const ANTEPARTUM_MIN_BATCH_COUNT = 4

export interface BatchRecommendation {
  patientId: string
  encounterType: EncounterType
  encounterIds: string[]
  oldestDays: number
  reason: string
}

/** Minimal encounter fields required for batch computation. */
export interface EncounterSummaryForBatch {
  id: string
  patientId: string
  type: EncounterType
  date?: string | null
  status: EncounterStatus
}

/**
 * Analyse authenticated encounters and return batch-ready groups per §6.4.3.
 * Sorted by oldest-first (highest urgency at the top).
 */
export const computeBatchRecommendations = (
  encounters: EncounterSummaryForBatch[],
): BatchRecommendation[] => {
  const authenticated = encounters.filter(e => e.status === EncounterStatus.AUTHENTICATED)

  // Group by patientId + encounterType.
  const groups = new Map<string, EncounterSummaryForBatch[]>()
  for (const enc of authenticated) {
    const key = `${enc.patientId}::${enc.type}`
    const curr = groups.get(key)
    if (curr) {
      curr.push(enc)
    } else {
      groups.set(key, [enc])
    }
  }

  const ready: BatchRecommendation[] = []

  for (const [key, encs] of groups) {
    const [patientId, encounterType] = key.split('::') as [string, EncounterType]
    const daysList = encs.map(e => getDaysSinceDOS(e.date) ?? 0)
    const oldestDays = Math.max(...daysList)
    const threshold = BATCH_THRESHOLDS[encounterType]
    if (threshold === undefined) continue

    let reason: string | null = null

    if (encounterType === EncounterType.ANTEPARTUM) {
      if (encs.length >= ANTEPARTUM_MIN_BATCH_COUNT) {
        reason = `${encs.length} authenticated antepartum visits — ready to batch`
      } else if (oldestDays >= threshold) {
        reason = `Oldest antepartum visit is ${oldestDays}d — batch now to avoid timely filing risk`
      }
    } else if (encounterType === EncounterType.BIRTHING_CENTER_ADMISSION) {
      if (oldestDays >= threshold) {
        reason = 'Delivery encounter — submit delivery claim within 15 days'
      }
      // §6.4.3: Labor management billing alert — independent of delivery claim threshold.
      if (oldestDays >= LABOR_MANAGEMENT_BATCH_THRESHOLD) {
        ready.push({
          patientId,
          encounterType,
          encounterIds: encs.map(e => e.id),
          oldestDays,
          reason: `Labor management billing — ${oldestDays}d elapsed, submit within 30 days of completion (AMA 2027)`,
        })
      }
    } else {
      if (oldestDays >= threshold) {
        reason = `${encounterTypeName[encounterType] ?? encounterType} is ${oldestDays}d — submit within timely filing window`
      }
    }

    if (reason) {
      ready.push({
        patientId,
        encounterType,
        encounterIds: encs.map(e => e.id),
        oldestDays,
        reason,
      })
    }
  }

  return ready.sort((a, b) => b.oldestDays - a.oldestDays)
}

/** §6.4.2: Determine the highest notification level for an encounter given practice thresholds. */
export type NotificationLevel = 'billing-alert' | 'critical-notice' | 'first-notice'
export const getNotificationLevel = (
  daysSinceDOS: number,
  thresholds: { firstNotice: number; criticalNotice: number; billingAlert: number },
): NotificationLevel | null => {
  if (daysSinceDOS >= thresholds.billingAlert) return 'billing-alert'
  if (daysSinceDOS >= thresholds.criticalNotice) return 'critical-notice'
  if (daysSinceDOS >= thresholds.firstNotice) return 'first-notice'
  return null
}
